import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Database,
  Code2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Maximize2,
  X,
  Play,
  CheckCircle2,
  AlertTriangle,
  FileCode
} from 'lucide-react';
import {
  DbDatabaseInfo,
  DbTableMeta,
  DbRowsResponse,
  DbQueryResponse,
  fetchDbTables,
  fetchTableRows,
  executeSafeQuery
} from '../../services/inspectService';

export const DatabaseTab: React.FC = () => {
  const [databases, setDatabases] = useState<DbDatabaseInfo[]>([]);
  const [tables, setTables] = useState<DbTableMeta[]>([]);
  const [selectedTable, setSelectedTable] = useState<string>('base_clips');
  const [activeSubTab, setActiveSubTab] = useState<'data' | 'schema' | 'sql'>('data');

  // Table rows & pagination
  const [rowsData, setRowsData] = useState<DbRowsResponse | null>(null);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [pageOffset, setPageOffset] = useState(0);
  const pageSize = 25;
  const isFetchingRowsRef = useRef(false);

  // JSON viewer modal
  const [activeJsonContent, setActiveJsonContent] = useState<{ title: string; data: any } | null>(null);

  // SQL Console
  const [customSql, setCustomSql] = useState<string>(
    'SELECT id, title, creator_name, duration, created_at FROM base_clips ORDER BY created_at DESC LIMIT 15'
  );
  const [sqlResult, setSqlResult] = useState<DbQueryResponse | null>(null);
  const [isExecutingSql, setIsExecutingSql] = useState(false);

  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [tablesError, setTablesError] = useState<string | null>(null);

  // Load database metadata once on mount
  const loadTables = useCallback(async () => {
    setIsLoadingTables(true);
    setTablesError(null);
    try {
      const data = await fetchDbTables();
      if (data) {
        setDatabases(data.databases);
        const filtered = (data.tables || []).filter(
          t => !t.name.startsWith('_') && !t.name.startsWith('-')
        );
        setTables(filtered);
        if (filtered.length > 0) {
          setSelectedTable(prev => (!prev || !filtered.some(t => t.name === prev)) ? filtered[0].name : prev);
        }
      } else {
        setTablesError('Could not load database tables from D1.');
      }
    } catch (err: any) {
      setTablesError(err.message || 'Error loading tables.');
    } finally {
      setIsLoadingTables(false);
    }
  }, []);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // Load table rows when table or pageOffset changes
  const loadRows = useCallback(async (table: string, offset: number) => {
    if (isFetchingRowsRef.current) return;
    isFetchingRowsRef.current = true;
    setIsLoadingRows(true);
    try {
      const data = await fetchTableRows({
        tableName: table,
        limit: pageSize,
        offset
      });
      if (data && data.success) {
        setRowsData(data);
      }
    } finally {
      setIsLoadingRows(false);
      isFetchingRowsRef.current = false;
    }
  }, [pageSize]);

  useEffect(() => {
    if (selectedTable) {
      loadRows(selectedTable, pageOffset);
    }
  }, [selectedTable, pageOffset, loadRows]);

  const handleSelectTable = (tblName: string) => {
    setSelectedTable(tblName);
    setPageOffset(0);
  };

  const handleRunQuery = async () => {
    if (!customSql.trim() || isExecutingSql) return;
    setIsExecutingSql(true);
    setSqlResult(null);
    try {
      const res = await executeSafeQuery(customSql.trim());
      setSqlResult(res);
    } finally {
      setIsExecutingSql(false);
    }
  };

  const currentTableMeta = tables.find(t => t.name === selectedTable);
  const totalRows = rowsData?.total || currentTableMeta?.rowCount || 0;
  const totalPages = Math.ceil(totalRows / pageSize) || 1;
  const currentPage = Math.floor(pageOffset / pageSize) + 1;

  // Detect and format cell value
  const renderCellContent = (colName: string, val: any) => {
    if (val === null || val === undefined) {
      return <span style={{ color: 'var(--accent-amber)', fontStyle: 'italic' }}>null</span>;
    }

    if (typeof val === 'number') {
      // If it looks like a unix timestamp in ms or s
      if (colName.includes('created_at') || colName.includes('updated_at') || colName.includes('added_at')) {
        const ms = val > 1e11 ? val : val * 1000;
        return (
          <span title={new Date(ms).toLocaleString()} style={{ fontFamily: 'var(--font-mono)' }}>
            {new Date(ms).toLocaleDateString()}
          </span>
        );
      }
      return <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-cyan)' }}>{val}</span>;
    }

    if (typeof val === 'string') {
      // Check if value is JSON
      if ((val.startsWith('{') && val.endsWith('}')) || (val.startsWith('[') && val.endsWith(']'))) {
        try {
          const parsed = JSON.parse(val);
          return (
            <button
              type="button"
              onClick={() => setActiveJsonContent({ title: colName, data: parsed })}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 7px',
                borderRadius: '4px',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                background: 'rgba(139, 92, 246, 0.15)',
                color: 'var(--accent-purple)',
                cursor: 'pointer',
                fontSize: '0.72rem',
                fontFamily: 'var(--font-mono)'
              }}
            >
              <FileCode size={11} />
              <span>JSON ({Array.isArray(parsed) ? `${parsed.length} items` : 'Object'})</span>
            </button>
          );
        } catch {
          // not valid JSON, fallthrough
        }
      }

      if (val.length > 35) {
        return (
          <span title={val} style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>
            {val.slice(0, 32)}…
          </span>
        );
      }

      return <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem' }}>{val}</span>;
    }

    return String(val);
  };

  return (
    <div>
      {/* Database Selector Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.25rem',
        padding: '0.75rem 1.25rem',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Database size={18} color="var(--accent-purple)" />
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Target Database:</span>
          {databases.map(db => (
            <span
              key={db.binding}
              style={{
                padding: '3px 10px',
                borderRadius: 'var(--radius-full)',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid var(--border-glow)',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 700,
                fontFamily: 'var(--font-mono)'
              }}
            >
              {db.binding} ({db.name})
            </span>
          ))}
          <button
            type="button"
            onClick={loadTables}
            disabled={isLoadingTables}
            title="Refresh database tables"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--border-glass)',
              borderRadius: 'var(--radius-sm)',
              width: '28px',
              height: '28px',
              color: 'var(--text-muted)',
              cursor: isLoadingTables ? 'not-allowed' : 'pointer',
              marginLeft: '4px'
            }}
          >
            <RefreshCw size={13} className={isLoadingTables ? 'spin' : ''} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => setActiveSubTab('data')}
            style={{
              padding: '6px 14px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeSubTab === 'data' ? 'var(--accent-purple)' : 'transparent',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: activeSubTab === 'data' ? 700 : 500,
              cursor: 'pointer'
            }}
          >
            Table Data
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('schema')}
            style={{
              padding: '6px 14px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeSubTab === 'schema' ? 'var(--accent-purple)' : 'transparent',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: activeSubTab === 'schema' ? 700 : 500,
              cursor: 'pointer'
            }}
          >
            Schema DDL
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('sql')}
            style={{
              padding: '6px 14px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeSubTab === 'sql' ? 'var(--accent-purple)' : 'transparent',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: activeSubTab === 'sql' ? 700 : 500,
              cursor: 'pointer'
            }}
          >
            Safe SQL Console
          </button>
        </div>
      </div>

      {/* Error alert banner if loading tables failed */}
      {tablesError && (
        <div style={{
          padding: '10px 16px',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#fca5a5',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem'
        }}>
          <span>{tablesError}</span>
          <button
            type="button"
            onClick={loadTables}
            style={{
              padding: '4px 10px',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '0.78rem'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Tables Selector Ribbon */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '1.25rem'
      }}>
        {isLoadingTables && tables.length === 0 ? (
          <div style={{ padding: '8px 12px', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            Loading tables from D1...
          </div>
        ) : tables.length === 0 ? (
          <div style={{ padding: '8px 12px', fontSize: '0.82rem', color: '#f87171', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>No tables found.</span>
            <button
              type="button"
              onClick={loadTables}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid var(--border-glass)',
                borderRadius: '4px',
                color: '#fff',
                padding: '2px 8px',
                fontSize: '0.75rem',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          tables.map(tbl => {
            const isSelected = selectedTable === tbl.name;
            return (
              <button
                key={tbl.name}
                type="button"
                onClick={() => handleSelectTable(tbl.name)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '5px 11px',
                  borderRadius: 'var(--radius-md)',
                  border: isSelected ? '1px solid var(--accent-purple)' : '1px solid var(--border-glass)',
                  background: isSelected ? 'rgba(139, 92, 246, 0.22)' : 'var(--bg-card)',
                  color: isSelected ? '#fff' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.78rem',
                  fontWeight: isSelected ? 700 : 500,
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{tbl.name}</span>
                <span style={{
                  padding: '1px 5px',
                  borderRadius: 'var(--radius-full)',
                  background: isSelected ? 'rgba(139, 92, 246, 0.35)' : 'rgba(255, 255, 255, 0.08)',
                  fontSize: '0.68rem',
                  color: isSelected ? '#d8b4fe' : 'var(--text-muted)'
                }}>
                  {tbl.rowCount}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* ────────────────────────────────────────────── */}
      {/* 1. TABLE DATA VIEW                             */}
      {/* ────────────────────────────────────────────── */}
      {activeSubTab === 'data' && (
        <div>
          {/* Table Header / Pagination bar */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '0.75rem',
            color: 'var(--text-muted)',
            fontSize: '0.8rem'
          }}>
            <div>
              Table <strong>"{selectedTable}"</strong> • Showing {rowsData?.rows?.length || 0} of {totalRows} rows
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={pageOffset === 0 || isLoadingRows}
                onClick={() => setPageOffset(prev => Math.max(0, prev - pageSize))}
                style={{
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-glass)',
                  background: 'var(--bg-glass)',
                  color: '#fff',
                  cursor: pageOffset === 0 ? 'not-allowed' : 'pointer',
                  opacity: pageOffset === 0 ? 0.4 : 1
                }}
              >
                <ChevronLeft size={14} />
              </button>
              <button
                type="button"
                disabled={currentPage >= totalPages || isLoadingRows}
                onClick={() => setPageOffset(prev => prev + pageSize)}
                style={{
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-glass)',
                  background: 'var(--bg-glass)',
                  color: '#fff',
                  cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage >= totalPages ? 0.4 : 1
                }}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>

          {/* Records Table */}
          <div style={{
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-card)',
            overflow: 'hidden'
          }}>
            {isLoadingRows ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <p>Reading rows from D1...</p>
              </div>
            ) : !rowsData?.rows || rowsData.rows.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                <p>No rows in table "{selectedTable}".</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: '68vh' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(0,0,0,0.3)', position: 'sticky', top: 0, zIndex: 1 }}>
                      {Object.keys(rowsData.rows[0] || {}).map(col => (
                        <th
                          key={col}
                          style={{
                            padding: '9px 12px',
                            textAlign: 'left',
                            color: 'var(--text-muted)',
                            fontFamily: 'var(--font-mono)',
                            fontSize: '0.74rem',
                            letterSpacing: '0.04em',
                            borderBottom: '1px solid var(--border-glass)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rowsData.rows.map((row, rIdx) => (
                      <tr
                        key={rIdx}
                        style={{
                          borderTop: rIdx > 0 ? '1px solid var(--border-glass)' : 'none',
                          background: rIdx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)'
                        }}
                      >
                        {Object.entries(row).map(([k, v], cIdx) => (
                          <td
                            key={cIdx}
                            style={{
                              padding: '8px 12px',
                              whiteSpace: 'nowrap',
                              color: 'var(--text-primary)'
                            }}
                          >
                            {renderCellContent(k, v)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ────────────────────────────────────────────── */}
      {/* 2. SCHEMA DDL VIEW                             */}
      {/* ────────────────────────────────────────────── */}
      {activeSubTab === 'schema' && (
        <div style={{ display: 'grid', gap: '1.25rem' }}>
          {currentTableMeta && (
            <>
              {/* Columns Table */}
              <div style={{
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-glass)',
                background: 'var(--bg-card)',
                padding: '1.25rem'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: '1rem' }}>
                  Columns for "{selectedTable}" ({currentTableMeta.columns.length} columns)
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                        <th style={thStyle}>CID</th>
                        <th style={thStyle}>Column</th>
                        <th style={thStyle}>Data Type</th>
                        <th style={thStyle}>Not Null</th>
                        <th style={thStyle}>Primary Key</th>
                        <th style={thStyle}>Default</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentTableMeta.columns.map((col) => (
                        <tr key={col.cid} style={{ borderTop: '1px solid var(--border-glass)' }}>
                          <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{col.cid}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-mono)' }}>
                            {col.name}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--accent-purple)', fontFamily: 'var(--font-mono)' }}>
                            {col.type}
                          </td>
                          <td style={tdStyle}>{col.notnull ? 'YES' : 'NO'}</td>
                          <td style={tdStyle}>
                            {col.pk ? (
                              <span style={{ color: 'var(--accent-amber)', fontWeight: 700 }}>PRIMARY KEY</span>
                            ) : '—'}
                          </td>
                          <td style={{ ...tdStyle, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {col.dflt_value === null ? 'null' : String(col.dflt_value)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Raw CREATE TABLE DDL */}
              <div style={{
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-glass)',
                background: 'var(--bg-card)',
                padding: '1.25rem'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#fff', marginBottom: '0.75rem' }}>
                  Raw SQL DDL
                </h3>
                <pre style={{
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-cosmic)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--accent-cyan)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.78rem',
                  overflowX: 'auto',
                  lineHeight: 1.5
                }}>
                  {currentTableMeta.sql}
                </pre>
              </div>
            </>
          )}
        </div>
      )}

      {/* ────────────────────────────────────────────── */}
      {/* 3. SAFE SQL CONSOLE                            */}
      {/* ────────────────────────────────────────────── */}
      {activeSubTab === 'sql' && (
        <div style={{ display: 'grid', gap: '1.25rem' }}>
          <div style={{
            padding: '1.25rem',
            borderRadius: 'var(--radius-lg)',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-glass)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Code2 size={18} color="var(--accent-purple)" />
                <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.9rem' }}>Read-Only SQL Query Runner</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--accent-emerald)', background: 'rgba(16, 185, 129, 0.15)', padding: '2px 8px', borderRadius: 'var(--radius-full)' }}>
                  SELECT Only • Enforced LIMIT 50
                </span>
              </div>

              {/* Sample Queries */}
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Base Clips', sql: 'SELECT id, title, creator_name, duration, created_at FROM base_clips ORDER BY created_at DESC LIMIT 15' },
                  { label: 'My Clips', sql: 'SELECT id, clip_id, performer_name, title, updated_at FROM my_clips ORDER BY updated_at DESC LIMIT 15' },
                  { label: 'Shared Clips', sql: 'SELECT id, clip_id, author_name, likes_count, views_count FROM shared_clips ORDER BY likes_count DESC LIMIT 15' },
                  { label: 'Top Performers', sql: 'SELECT username, takes_count, total_duration FROM users ORDER BY takes_count DESC LIMIT 10' }
                ].map(sample => (
                  <button
                    key={sample.label}
                    type="button"
                    onClick={() => setCustomSql(sample.sql)}
                    style={{
                      padding: '3px 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border-glass)',
                      background: 'var(--bg-glass)',
                      color: 'var(--text-muted)',
                      fontSize: '0.72rem',
                      cursor: 'pointer'
                    }}
                  >
                    {sample.label}
                  </button>
                ))}
              </div>
            </div>

            <textarea
              value={customSql}
              onChange={(e) => setCustomSql(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-cosmic)',
                border: '1px solid var(--border-glow)',
                color: '#fff',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.82rem',
                outline: 'none',
                resize: 'vertical',
                lineHeight: 1.4
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={handleRunQuery}
                disabled={isExecutingSql || !customSql.trim()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 20px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: 'linear-gradient(135deg, var(--accent-emerald) 0%, var(--accent-cyan) 100%)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  cursor: isExecutingSql ? 'not-allowed' : 'pointer'
                }}
              >
                <Play size={14} />
                <span>{isExecutingSql ? 'Executing...' : 'Run Query'}</span>
              </button>
            </div>
          </div>

          {/* Query Results */}
          {sqlResult && (
            <div style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-glass)',
              background: 'var(--bg-card)',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '10px 14px',
                background: 'rgba(0,0,0,0.3)',
                borderBottom: '1px solid var(--border-glass)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.78rem'
              }}>
                <span style={{ color: sqlResult.success ? 'var(--accent-emerald)' : 'var(--accent-coral)' }}>
                  {sqlResult.success ? `Returned ${sqlResult.rowCount} rows` : `Query Error: ${sqlResult.error}`}
                </span>
                {sqlResult.durationMs !== undefined && (
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    Executed in {sqlResult.durationMs}ms
                  </span>
                )}
              </div>

              {sqlResult.success && sqlResult.rows && sqlResult.rows.length > 0 && (
                <div style={{ overflowX: 'auto', maxHeight: '55vh' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                        {Object.keys(sqlResult.rows[0]).map(col => (
                          <th key={col} style={thStyle}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sqlResult.rows.map((row, idx) => (
                        <tr key={idx} style={{ borderTop: idx > 0 ? '1px solid var(--border-glass)' : 'none' }}>
                          {Object.entries(row).map(([k, v], cIdx) => (
                            <td key={cIdx} style={tdStyle}>{renderCellContent(k, v)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* JSON Viewer Modal */}
      {activeJsonContent && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalBoxStyle, width: '620px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff' }}>
                Column: {activeJsonContent.title}
              </h3>
              <button onClick={() => setActiveJsonContent(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>
            <pre style={{
              padding: '1rem',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-cosmic)',
              border: '1px solid var(--border-glass)',
              color: '#fff',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.74rem',
              maxHeight: '60vh',
              overflow: 'auto',
              lineHeight: 1.45
            }}>
              {JSON.stringify(activeJsonContent.data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '8px 12px',
  textAlign: 'left',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.74rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px'
};

const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.75)',
  backdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1100,
  padding: '1rem'
};

const modalBoxStyle: React.CSSProperties = {
  background: 'var(--bg-deep)',
  border: '1px solid var(--border-glow)',
  borderRadius: 'var(--radius-lg)',
  padding: '1.5rem',
  boxShadow: 'var(--shadow-lg)'
};
